// graphqlFileUpload.ts
import { useEffect, useRef } from 'react';
import { useDispatch } from 'react-redux';
import { toast } from 'react-toastify';

import { OperationVariables, useApolloClient } from '@apollo/client';
import { setError, setLoading } from '@common/slices';
import { extractMessage } from '@utils/helper-utils';
import { resolveGraphqlBaseUrl } from '@utils/tenant-utils';
import { getUserToken } from '@utils/userUtils';

import { DocumentNode, print } from 'graphql';

export function useApiUploader<TData = any, TVariables = OperationVariables>() {
  const dispatch = useDispatch();
  const client = useApolloClient();
  const abortControllerRef = useRef<AbortController | null>(null);

  // Cancel ongoing upload on unmount
  useEffect(() => {
    return () => {
      abortControllerRef.current?.abort();
    };
  }, []);

  async function upload(
    query: DocumentNode,
    variables: TVariables,
    file: File,
    options?: {
      key?: string; // for loading/error dispatch
      successMessage?: string;
      successCondition?: (body: { data: TData }) => boolean;
      errorMessage?: string; // optional
      onErrorCallback?: (body: { data: TData }) => void;
      withLoader?: boolean;
      suppressErrorToast?: boolean;
      refetchQueries?: string[] | DocumentNode[];
    }
  ): Promise<{ data: TData }> {
    const {
      key,
      successMessage,
      successCondition,
      errorMessage,
      onErrorCallback,
      withLoader = true,
      suppressErrorToast = false,
      refetchQueries
    } = options || {};

    if (withLoader && key) dispatch(setLoading({ key, value: true }));
    if (key) dispatch(setError({ key, error: null }));

    abortControllerRef.current = new AbortController();

    const formData = new FormData();
    formData.append('formFile', file, file.name);

    formData.append(
      'operations',
      JSON.stringify({
        query: print(query),
        variables: {
          ...variables,
          documentName: file.name,
          document: null
        }
      })
    );

    formData.append(
      'map',
      JSON.stringify({
        formFile: ['variables.document']
      })
    );

    let body: { data: TData; errors: any };

    try {
      const response = await fetch(`${resolveGraphqlBaseUrl()}/graphql`, {
        method: 'POST',
        headers: {
          authorization: `Bearer ${getUserToken()}` || null,
          'GraphQL-preflight': '1'
        },
        body: formData,
        signal: abortControllerRef.current.signal
      });
      body = await response.json();

      const success =
        body?.errors && body?.errors?.length !== 0
          ? false
          : (successCondition?.(body) ?? true);

      if (success && successMessage) {
        toast.success(successMessage, { autoClose: 3000 });
      }

      if (success && refetchQueries && refetchQueries.length > 0) {
        try {
          await client.refetchQueries({
            include: refetchQueries
          });
        } catch (refetchError) {
          console.warn('Failed to refetch queries after upload:', refetchError);
        }
      }

      if (!success) {
        if (!suppressErrorToast) {
          if (body.errors?.length > 0) {
            toast.error(extractMessage(body.errors?.[0]?.message), {
              autoClose: 3000
            });
          } else if (errorMessage) {
            toast.error(errorMessage, { autoClose: 3000 });
          }
        }
        onErrorCallback?.(body);
        if (key) dispatch(setError({ key, error: errorMessage }));
      }
    } catch (err: any) {
      const errorMsg = err.message ?? 'Upload failed';
      if (!suppressErrorToast) {
        toast.error(errorMsg, { autoClose: 3000 });
      }
      if (key) dispatch(setError({ key, error: errorMsg }));
      throw err;
    } finally {
      if (withLoader && key) dispatch(setLoading({ key, value: false }));
    }

    return body;
  }

  return upload;
}
